import { Stack } from "@mui/material";
import range from "lodash/range";
import type { FC, JSX } from "react";
import type { Currency } from "../../../data/BaseEntity";
import type { Price } from "../../../misc/price";
import { useProductsTotalPrice } from "../../../sharedComponents/products/price/useProductsTotalPrice";
import { getCalculationExchangeRate } from "../../calculationUtils";

export interface Product {
  readonly id: string;
  readonly date: string;
  readonly currency: Currency;
  readonly options: { readonly id: string | undefined; readonly quantity: number }[];
}

interface DayPriceProps {
  readonly dayShift: number;
  readonly currency: Currency | undefined;
  readonly numberOfPeople: number;
  readonly price: Price;
}

const DayPrice: FC<DayPriceProps> = ({ dayShift, currency, numberOfPeople, price }) => {
  const priceWithCurrency = price.toDisplayValue(currency);
  const pricePerPerson =
    numberOfPeople && price.isPresent() && currency
      ? `${price.divideBy(numberOfPeople).round().toDisplayValue(currency)}/os.`
      : undefined;
  if (!currency) {
    return (
      <Stack
        data-testid={`day-price-${dayShift}`}
        sx={{
          alignItems: "center",
        }}
      >
        <div>{dayShift > 0 ? `+${dayShift}` : `${dayShift}`}</div>
      </Stack>
    );
  }
  return (
    <Stack
      data-testid={`day-price-${dayShift}`}
      sx={{
        alignItems: "center",
      }}
    >
      <div>{dayShift > 0 ? `+${dayShift}` : `${dayShift}`}</div>
      <div> {priceWithCurrency}</div>
      {numberOfPeople && <div>{pricePerPerson}</div>}
    </Stack>
  );
};

interface Props {
  readonly selectedProducts: Omit<Product, "currency">[];
  readonly availableProducts: { id: string; currency: Currency }[];
  readonly currency: Currency | undefined;
  readonly margin: number;
  readonly numberOfPeople: number;
  readonly children?: JSX.Element;
}

const SurroundingDaysPrice: FC<Props> = ({
  selectedProducts,
  availableProducts,
  currency,
  margin,
  numberOfPeople,
  children,
}) => {
  const products = selectedProducts.map((p) => {
    const currency = availableProducts.find((pr) => p.id == pr.id)?.currency;
    return { ...p, currency };
  });
  const exchangeRate = getCalculationExchangeRate(selectedProducts, currency, products);
  const previousDays = range(-4, 0);
  const nextDays = range(1, 5);
  const { prices } = useProductsTotalPrice(
    products,
    currency,
    margin,
    exchangeRate,
    { from: -4, to: 5 },
    numberOfPeople,
  );
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        alignItems: "center",
        justifyContent: "flex-end",
      }}
    >
      {previousDays.map((d) => (
        <DayPrice key={d} dayShift={d} currency={currency} numberOfPeople={numberOfPeople} price={prices[d] as Price} />
      ))}
      {children}
      {nextDays.map((d) => (
        <DayPrice key={d} dayShift={d} currency={currency} numberOfPeople={numberOfPeople} price={prices[d] as Price} />
      ))}
    </Stack>
  );
};

export default SurroundingDaysPrice;
